import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton, Typography, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from "react";
import { useUser } from "../../context/userContext";
import { updateSalesByAdmin } from "../../api/salesApi";
import { createLog } from "../../api/logApi";
import { toNullIfEmptyOrZero } from "../../utils/priceUtils";
import handleAgentName from "../../utils/userUtils";
import GenericSalesForm from "../common/genericSalesForm";
import { getPricing } from "../../api/resourceApi";
import { convertDateStringToDateTime, convertDateTimeToDateString } from "../../utils/datetimeUtils";
import LogViewer from "../common/logViewer";
import { useRef } from "react";

export default function EditAdminSalesModal({ show, onHide, selectedSale, onSaved }) {
    const { user } = useUser();
    const isSuperAdmin = user?.roles.includes("Super Admin");
    const isAsternAdmin = user?.roles?.includes("Astern Admin");
    const canEditPricing = !isAsternAdmin && user?.roles?.some(r => ['Accountant', 'Super Admin'].includes(r));

    // Allow Admin, Super Admin, and Accountant to edit original price
    const canEditOriPrice = canEditPricing;

    const availablePaymentMethods = [
        { label: "CASH", value: "CASH" },
        { label: "CREDIT CARD 24 MONTHS", value: "CREDITCARD24" },
        { label: "CREDIT CARD 36 MONTHS", value: "CREDITCARD36" },
        { label: "CREDIT CARD 60 MONTHS", value: "CREDITCARD60" }
    ];

    const [originalPrice, setOriginalPrice] = useState();
    const [finalPrice, setFinalPrice] = useState();
    const [addOnPrice, setAddOnPrice] = useState();
    const [asternDiscount, setAsternDiscount] = useState();
    const [agentDiscount, setAgentDiscount] = useState();

    const [customerName, setCustomerName] = useState();
    const [customerEmail, setCustomerEmail] = useState();
    const [customerPhone, setCustomerPhone] = useState();
    const [customerAddress, setCustomerAddress] = useState();

    // New State for Product/Category Logic
    const [productList, setProductList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("RESIDENTIAL");
    const [panelQty, setPanelQty] = useState(0);
    const [energy, setEnergy] = useState(0);
    const [residentialMatch, setResidentialMatch] = useState(null);
    const [canEditPrice, setCanEditPrice] = useState(false);
    // const [productOptions, setProductOptions] = useState([]);
    // const [selectedProductId, setSelectedProductId] = useState();
    const [paymentMethod, setPaymentMethod] = useState();
    const [depositOn, setDepositOn] = useState();
    const [remarks, setRemarks] = useState();

    // Check if user has manually interacted with form to avoid auto-overwriting custom prices on load
    const userModified = useRef(false);

    const fetchProducts = async () => {
        try {
            const response = await getPricing();
            if (response.status === 200) {
                setProductList((response.data || []).filter(r => r.listName === 'Residential Pricing'));
            }
            else {
                console.error("Unexpected response:", response);
            }
        }
        catch (error) {
            console.error("Error fetching pricing:", error);
        }
    }

    useEffect(() => {
        if (show && selectedSale) {
            setOriginalPrice(selectedSale.originalPrice);
            setFinalPrice(selectedSale.finalPrice);
            setAddOnPrice(selectedSale.addOnPrice);
            setAsternDiscount(selectedSale.discountByAstern);
            setAgentDiscount(selectedSale.discountByAgent);

            setCustomerName(selectedSale.customerDetails?.customerName ?? "");
            setCustomerEmail(selectedSale.customerDetails?.email ?? "");
            setCustomerPhone(selectedSale.customerDetails?.phoneNumber ?? "");
            setCustomerAddress(selectedSale.customerDetails?.address ?? "");

            setSelectedCategory(selectedSale.productCategory);
            setPanelQty(selectedSale.panelQty || 0);
            setEnergy(selectedSale.energy || 0);

            setRemarks("");

            if (selectedSale.productCategory === "RESIDENTIAL") {
                // For Residential, we may want to auto-match product later
                setResidentialMatch(true); // Reset first
            }
            else {
                setResidentialMatch(); // Not applicable
            }

            // If user can edit price, fetch all products for matching logic
            if (canEditOriPrice) {
                fetchProducts();
            }
            else {
                setProductList([]); // Not available for others
            }
            setPaymentMethod(selectedSale.paymentMethod ?? "");
            setProductList([]);
            setDepositOn(selectedSale.depositOn ?? "");

            // Reset modification flag when a new sale is loaded
            userModified.current = false;
        }
        else if (!show && !selectedSale) {
            setOriginalPrice();
            setFinalPrice();
            setAddOnPrice();
            setAsternDiscount();
            setAgentDiscount();

            setCustomerName();
            setCustomerEmail();
            setCustomerPhone();
            setCustomerAddress();

            setPaymentMethod();
            setProductList([]);

            setDepositOn();
        }
    }, [show, selectedSale, isSuperAdmin]);

    // ===== Calculate Energy (kWp) =====
    // Only calculate if panelQty changes and we are editing (not just initial load)
    // However, for simplicity in this modal, we might want to trust the user input or auto-calc
    // mirroring createSalesForm:
    // Note: In createSalesForm, energy is set when panelQty changes. 
    // Here we might need to be careful not to overwrite existing energy if it was custom?
    // But usually energy = panelQty * 0.62.

    // ===== Set Price Editing Permission Based on Category =====
    useEffect(() => {
        // Commercial can edit price, Residential cannot (unless super admin overrides? logic says canEditOriPrice is isAdmin || isSuperAdmin)
        // But we also want to follow the form logic:
        const isCommercial = selectedCategory === "COMMERCIAL";
        // If user is admin/superadmin, they can generally edit, but for Residential we want auto-populating.
        // Let's stick to the form logic: Residential = auto, Commercial = manual.
        // But since this is an ADMIN edit modal, maybe we allow override?
        // The original code had `canEditOriPrice = isAdmin || isSuperAdmin`.
        // We should probably respect that, but also provide the auto-match feature.
        setCanEditPrice(isCommercial || canEditOriPrice);
    }, [selectedCategory, canEditOriPrice]);

    // ===== RESIDENTIAL LOGIC: Auto-populate Price =====
    // Match by kWp against ResourcePricing. Prefer 1-phase price, fall back to 3-phase.
    useEffect(() => {
        if (selectedCategory === "RESIDENTIAL" && energy > 0 && canEditOriPrice && userModified.current) {
            const match = productList.find(p => Number(p.energy) === Number(energy));
            setResidentialMatch(match);

            if (match && paymentMethod) {
                const fieldMap = {
                    'CASH':         ['cashPrice',  'cash3p8k'],
                    'creditCard36': ['cC36Price',  'cC36_3p8k'],
                    'creditCard60': ['cC60Price',  'cC60_3p8k'],
                };
                const [f1, f3] = fieldMap[paymentMethod] || [];
                const p1 = f1 ? Number(match[f1]) : 0;
                const p3 = f3 ? Number(match[f3]) : 0;
                setOriginalPrice(p1 > 0 ? p1 : p3);
            }
        }
    }, [selectedCategory, panelQty, energy, paymentMethod, productList, canEditOriPrice]);

    useEffect(() => {
        const oriPrice = originalPrice || 0;
        const asternDisc = asternDiscount || 0;
        const agentDisc = agentDiscount || 0;
        const afterDisc = oriPrice - asternDisc - agentDisc;

        setFinalPrice(Math.max(0, afterDisc));

    }, [originalPrice, asternDiscount, agentDiscount]);

    // const handleProductOptionsChange = (e) => {
    //     const productId = e.target.value;
    //     setSelectedProductId(productId);
    //     const selectedProduct = productOptions.find(p => String(p.productId) === String(productId));
    //     setOriginalPrice(selectedProduct?.price ?? 0);

    //     if (selectedProduct) {
    //         setPaymentMethod(selectedProduct.paymentMethod);
    //     }
    //     else {
    //         setPaymentMethod("");
    //     }
    // };

    const handleSaveChange = async () => {
        // No residential match: allow manual price entry (originalPrice validation below catches RM 0).

        const salesData = {
            "salesId": selectedSale.salesId,
            "updateSalesBySalesIdDto": {
                "agentId": selectedSale.agentDetails.userId,
                "coAgentId": selectedSale.coAgentDetails?.userId ?? null,
                "discountByAstern": asternDiscount || null,
                "discountByAgent": agentDiscount || null,
                "panelQty": panelQty,
                "energy": energy,
                "originalPrice": originalPrice,
                "finalPrice": finalPrice,
                "addOnPrice": addOnPrice || null,
                "productCategory": selectedCategory,
                "paymentMethod": paymentMethod,
                "status": selectedSale.status || null,
                "depositOn": convertDateStringToDateTime(depositOn) || null
            },
            "updateCustomerBySalesId": {
                "customerName": customerName,
                "email": customerEmail,
                "phoneNumber": customerPhone,
                "address": customerAddress
            },
            "updatedBy": user.username,
            "remarks": remarks || null
        };

        try {
            const updateSales = await updateSalesByAdmin(salesData);

            if (updateSales.status === 200) {
                try {
                    const logData = {
                        "module": "Sales",
                        "recordId": selectedSale.salesId,
                        "action": "Update",
                        "oldValue": JSON.stringify(selectedSale),
                        "newValue": JSON.stringify(updateSales.data),
                        "remarks": remarks,
                        "createdBy": user.username ?? ""
                    };

                    const logResponse = await createLog(logData);

                    if (logResponse.status === 200) {
                        console.log("Log created successfully");
                    }
                }
                catch (error) {
                    console.error("Failed to create log");
                }

                if (onSaved) {
                    onSaved();
                }

                onHide();
                alert("Sales updated successfully.");
            }
        } catch (error) {
            console.error("Failed to update sales: ", error);
        }
    };

    if (!show) {
        return null;
    }

    return (
        <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <span>
                    Edit Sales
                    <Typography component="span" sx={{ fontSize: '0.8rem', color: '#64748B', ml: 1, fontFamily: "'Poppins', sans-serif" }}>
                        ({selectedSale.customerDetails?.customerName})
                    </Typography>
                </span>
                <IconButton onClick={onHide} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                <div className="editSalesForm-container">
                    <form className="createSalesForm-form">
                        <GenericSalesForm
                            mode="edit"
                            isSuperAdmin={isSuperAdmin}
                            canEditOriPrice={canEditOriPrice}
                            agentGroup={selectedSale.agentDetails?.groupName}
                            agentName={handleAgentName(selectedSale.agentDetails?.firstName, selectedSale.agentDetails?.lastName, selectedSale.agentDetails?.alias)}

                            //Co-broke props for read-only display
                            coBrokeGroupName={selectedSale.coAgentDetails?.groupName}
                            coBrokeAgentName={selectedSale.coAgentDetails
                                ? handleAgentName(selectedSale.coAgentDetails.firstName, selectedSale.coAgentDetails.lastName, selectedSale.coAgentDetails.alias)
                                : ""}

                            coBrokeGroupOptions={[]} // Not editable in edit
                            coBrokeNameOptions={[]}   // Not editable in edit
                            selectedCoBrokeGroupId={selectedSale.coAgentDetails?.groupId}
                            setSelectedCoBrokeGroupId={() => { }}
                            selectedCoBrokeAgentId={selectedSale.coAgentDetails?.userId}
                            setSelectedCoBrokeAgentId={() => { }}
                            customerName={customerName}
                            setCustomerName={setCustomerName}
                            customerEmail={customerEmail}
                            setCustomerEmail={setCustomerEmail}
                            customerPhone={customerPhone}
                            setCustomerPhone={setCustomerPhone}
                            customerAddress={customerAddress}
                            setCustomerAddress={setCustomerAddress}
                            // Pass the new state variables
                            selectedCategory={selectedCategory}
                            setSelectedCategory={(val) => { setSelectedCategory(val); userModified.current = true; }}
                            panelQty={panelQty}
                            setPanelQty={(val) => {
                                setPanelQty(val);
                                setEnergy(Number((val * 0.62).toFixed(2)));
                                userModified.current = true;
                            }}
                            energy={energy}
                            setEnergy={setEnergy}
                            originalPrice={originalPrice}
                            setOriginalPrice={setOriginalPrice}
                            residentialMatch={residentialMatch}
                            availablePaymentMethods={availablePaymentMethods}
                            paymentMethod={paymentMethod}
                            setPaymentMethod={(val) => { setPaymentMethod(val); userModified.current = true; }}
                            canEditPrice={canEditPrice}
                            depositOn={depositOn}
                            setDepositOn={setDepositOn}
                            asternDiscount={asternDiscount}
                            setAsternDiscount={setAsternDiscount}
                            agentDiscount={agentDiscount}
                            setAgentDiscount={setAgentDiscount}
                            finalPrice={finalPrice}
                            setFinalPrice={setFinalPrice}
                            addOnPrice={addOnPrice}
                            setAddOnPrice={setAddOnPrice}
                            disableAgentFields={true}
                            disableCustomerFields={isAsternAdmin}
                            disableProductFields={isAsternAdmin}
                            disablePricingFields={isAsternAdmin || !canEditPricing}
                            handleAgentGroupChange={() => { }}
                        />

                        {/* Remarks & Log */}
                        <LogViewer
                            module="Sales"
                            recordId={selectedSale?.salesId}
                            salesId={selectedSale?.salesId}
                            remarks={remarks}
                            setRemarks={setRemarks}
                            showOnly="sales"
                        />
                    </form>
                </div>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <MuiButton onClick={onHide} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#64748B' }}>Cancel</MuiButton>
                <MuiButton onClick={handleSaveChange} variant="contained"
                    sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' } }}>
                    Save Changes
                </MuiButton>
            </DialogActions>
        </Dialog>
    )
}